import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "quill-mention";
import "quill-mention/dist/quill.mention.css";
import { Box, ClickAwayListener, FormControl, FormHelperText, GlobalStyles, InputLabel, alpha } from "@mui/material";
import "react-quill/dist/quill.core.css";
import { customFontSizes, getFontNameForClass, RichTextFieldCustomToolbar } from "../ToolbarComponents";
import NotchedOutline from "@mui/material/OutlinedInput/NotchedOutline";
import { QuillMentionMenu } from "./QuillMentionMenu";
import Delta from "quill-delta/lib/delta";
const quillWrapperSxProps = (fonts, isTextFieldExpanded) => (Object.assign({ position: "relative", width: "100%", height: "100%", display: "flex", flexDirection: "column", borderRadius: "8px", backgroundColor: ({ palette }) => alpha(palette.common.white, 0.75), ".ql-editor": {
        maxHeight: isTextFieldExpanded ? "100%" : "260px",
        overflowY: "auto",
        fontSize: theme => theme.typography.fontSize,
        fontFamily: theme => theme.typography.fontFamily,
        padding: "14px 8px 14px 16px"
    }, 
    // Style editor's placeholder
    ".ql-editor.ql-blank::before": {
        fontFamily: "Open Sans",
        letterSpacing: "0.15px",
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.4375em",
        color: theme => theme.palette.text.disabled,
        fontStyle: "normal",
        marginTop: "-1px"
    }, 
    // Set bold and italic icon color when active
    ".ql-bold.ql-active, .ql-italic.ql-active": {
        color: theme => theme.palette.primary.main,
        backgroundColor: theme => theme.palette.action.selected
    }, 
    // Set fonts according to the className (given by the font name)
    // This needs to be extended with the font family names that are available in the studio
    ".ql-font-Roboto": {
        fontFamily: "Roboto"
    }, ".ql-font-Arial": {
        fontFamily: "Arial"
    }, ".ql-font-Poppins": {
        fontFamily: "Poppins"
    }, ".ql-font-Verdana": {
        fontFamily: "Verdana"
    }, ".ql-font-Georgia": {
        fontFamily: "Georgia"
    } }, fonts.reduce((acc, font) => (Object.assign(Object.assign({}, acc), { [`.ql-font-${getFontNameForClass(font.name)}`]: { fontFamily: `'${font.name}'` } })), {})));
const textFieldSxProps = (focused, disabled, error) => ({
    width: "100%",
    minWidth: "250px",
    height: "100%",
    "& .MuiInputLabel-root": {
        "&.MuiInputLabel-outlined": {
            "&.MuiInputLabel-shrink": {
                transform: "translate(16px, -9px) scale(0.75)"
            },
            "&:not(.MuiInputLabel-shrink)": {
                transform: "translate(14px, 10px) scale(1)",
                "&.MuiInputLabel-sizeSmall": {
                    transform: "translate(14px, 6px) scale(1)"
                }
            }
        },
        "&.Mui-error": {
            "&:not(.MuiInputLabel-shrink)": {
                color: "text.secondary"
            }
        }
    },
    "& .MuiFormHelperText-root": {
        "&:before": {
            display: "none"
        },
        display: !focused && !error ? "none" : undefined,
        "&:not(.Mui-error)": {
            color: "primary.main"
        }
    },
    "& .rich-text-field": {
        borderStyle: "solid",
        borderWidth: "1px",
        borderColor: disabled ? "grey.200" : error ? "error.main" : focused ? "primary.main" : "divider"
    },
    "&:hover": {
        "& .rich-text-field": {
            borderColor: !disabled && !error && !focused ? "primary.light" : undefined
        }
    }
});
const initializeQuill = (fonts) => {
    // Override the default icons
    const icons = Quill.import("ui/icons");
    icons.bold = null;
    icons.italic = null;
    icons.align = null;
    icons.color = null;
    icons.background = null;
    // Override the default fonts and font sizes formats
    const fontSizes = Quill.import("attributors/style/size");
    fontSizes.whitelist = customFontSizes.map(size => `${size}px`);
    Quill.register(fontSizes, true);
    if (fonts) {
        const customFonts = Quill.import("formats/font");
        customFonts.whitelist = fonts;
        Quill.register(fonts, true);
    }
};
const generateFontFaces = (fontGroups) => {
    return fontGroups.flatMap(group => group.fonts.map(font => font.webFontUrl ? `@font-face { font-family: '${font.name}'; src: url(${font.webFontUrl}); font-display: swap; }` : "")).join("");
};
export const isMentionOperation = (op) => { var _a; return !!((_a = op === null || op === void 0 ? void 0 : op.insert) === null || _a === void 0 ? void 0 : _a.mention); };
export const QuillRichText = ({ id, quillRef, value, label, placeholder, helperText, error, disabled, onChange, onExceedCharacterLimit, onFocus, onBlur, formats, isTextFieldExpanded, handleExpand, enableExpansion, fonts, defaultFontSize, defaultFont, defaultColor, defaultBold, defaultItalic, predefinedColors, dataTestId, dataTrackingId, disableClickAwayListener, mentionConfiguration, charactersLimit, endAdornment, autoFocus, autoSelect, shouldEventNotTriggerClickAway, pronunciationConfiguration, speedConfiguration, aiTextEditingConfiguration }) => {
    var _a, _b;
    const [isTextFieldFocused, setIsTextFieldFocused] = React.useState(false);
    const joinedFonts = fonts.flatMap(group => group.fonts);
    const fontFaces = generateFontFaces(fonts); // Generate font faces styles
    const numOfMentionsRef = React.useRef(((mentionConfiguration === null || mentionConfiguration === void 0 ? void 0 : mentionConfiguration.enableMentions) && ((_a = value === null || value === void 0 ? void 0 : value.ops) === null || _a === void 0 ? void 0 : _a.filter(isMentionOperation).length)) || 0);
    const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
    const [mentionSearchTerm, setMentionSearchTerm] = useState("");
    const mentionMenuPositionOffsetRef = React.useRef();
    const onKeyDownCallbackRef = React.useRef(null);
    // by default clicking on the mention display it as selected but the range.length is 0 so formatting can't be applied
    React.useEffect(() => {
        initializeQuill(fonts === null || fonts === void 0 ? void 0 : fonts.map(font => font.fonts).flat().map(font => getFontNameForClass(font.name)));
        quillRef.current.getEditor().on("text-change", (delta, oldContents) => {
            if (charactersLimit) {
                const textLength = quillRef.current.getEditor().getLength();
                const numOfCharsOverLimit = textLength - numOfMentionsRef.current - 1 - charactersLimit;
                if (numOfCharsOverLimit > 0) {
                    quillRef.current.getEditor().setContents(oldContents, "user");
                    if (onExceedCharacterLimit)
                        onExceedCharacterLimit();
                }
            }
        });
        quillRef.current.getEditor().once("text-change", (delta, oldContents) => {
            if (autoFocus) {
                const length = quillRef.current.getEditor().getLength();
                quillRef.current.getEditor().setSelection(length - 1, 0, "user");
            }
        });
        quillRef.current.getEditor().clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
            if (!(mentionConfiguration === null || mentionConfiguration === void 0 ? void 0 : mentionConfiguration.enableMentions)) {
                return delta;
            }
            const ops = delta.ops.map((op) => {
                if (isMentionOperation(op)) {
                    op.insert = op.insert.mention.value;
                }
                return op;
            });
            return new Delta(ops);
        });
        const mentionClickedListener = () => {
            const range = quillRef.current.getEditor().getSelection();
            quillRef.current.getEditor().setSelection({ index: range.index, length: 1 }, "user");
        };
        window.addEventListener("mention-clicked", mentionClickedListener);
        return () => {
            window.removeEventListener("mention-clicked", mentionClickedListener);
        };
    }, []);
    const handleFocus = () => {
        if (!disabled && !isTextFieldFocused) {
            if (onFocus)
                onFocus();
            setIsTextFieldFocused(true);
            if (autoSelect) {
                const length = quillRef.current.getEditor().getLength();
                quillRef.current.getEditor().setSelection(0, length, "user");
            }
        }
    };
    const handleUnFocus = () => {
        if (isTextFieldFocused) {
            setIsTextFieldFocused(false);
            closeMentionMenu();
            if (onBlur && !disabled) {
                const unprivilegedEditor = quillRef.current.makeUnprivilegedEditor(quillRef.current.editor);
                onBlur(unprivilegedEditor);
            }
        }
    };
    const handleClickAway = (event) => {
        if (!disableClickAwayListener) {
            if (!(shouldEventNotTriggerClickAway === null || shouldEventNotTriggerClickAway === void 0 ? void 0 : shouldEventNotTriggerClickAway(event))) {
                handleUnFocus();
            }
        }
    };
    const handleBlur = () => {
        if (disableClickAwayListener) {
            handleUnFocus();
        }
    };
    const changeHandler = (value, delta, source, editor) => {
        if (source === "user" && !disabled) {
            if (mentionConfiguration === null || mentionConfiguration === void 0 ? void 0 : mentionConfiguration.enableMentions) {
                numOfMentionsRef.current = editor.getContents().ops.filter(isMentionOperation).length;
            }
            onChange(value, delta, source, editor);
        }
    };
    const source = React.useCallback((searchTerm) => {
        var _a;
        if (numOfMentionsRef.current >= (mentionConfiguration === null || mentionConfiguration === void 0 ? void 0 : mentionConfiguration.maxNumberOfMentions)) {
            return;
        }
        if (!mentionMenuOpen) {
            const boundsStatic = quillRef.current.getEditor().getBounds(quillRef.current.getEditor().getSelection().index);
            mentionMenuPositionOffsetRef.current = {
                left: (boundsStatic === null || boundsStatic === void 0 ? void 0 : boundsStatic.right) || 0,
                top: ((boundsStatic === null || boundsStatic === void 0 ? void 0 : boundsStatic.bottom) || 0) - ((_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.getEditingArea().clientHeight)
            };
            setMentionMenuOpen(true);
        }
        setMentionSearchTerm(searchTerm);
    }, []);
    const closeMentionMenu = React.useCallback(() => {
        mentionMenuPositionOffsetRef.current = null;
        setMentionSearchTerm("");
        setMentionMenuOpen(false);
    }, []);
    const includeToolbar = React.useMemo(() => !disabled && (formats.filter(format => format !== "mention").length > 0 || (pronunciationConfiguration === null || pronunciationConfiguration === void 0 ? void 0 : pronunciationConfiguration.enablePronunciation) ||
        (speedConfiguration === null || speedConfiguration === void 0 ? void 0 : speedConfiguration.enableSpeed) || aiTextEditingConfiguration), []);
    const modules = React.useMemo(() => {
        let modules = includeToolbar ? ({ toolbar: { container: `#toolbar-${id}` } }) : {};
        if (mentionConfiguration === null || mentionConfiguration === void 0 ? void 0 : mentionConfiguration.enableMentions) {
            modules["mention"] = {
                mentionDenotationChars: [mentionConfiguration.mentionTrigger],
                allowedChars: /^[a-zA-Z0-9_ ]*$/,
                spaceAfterInsert: false,
                showDenotationChar: false,
                source,
                onBeforeClose: closeMentionMenu
            };
        }
        return modules;
    }, []);
    const textLengthForCharacterLimit = charactersLimit && ((_b = quillRef === null || quillRef === void 0 ? void 0 : quillRef.current) === null || _b === void 0 ? void 0 : _b.getEditor().getLength());
    const helperTextWithCounterIfNeeded = useMemo(() => {
        if (charactersLimit) {
            const valueLength = textLengthForCharacterLimit - numOfMentionsRef.current - 1;
            return (_jsxs("span", { style: { display: "flex" }, children: [_jsx("span", { style: { flexGrow: 1 }, children: helperText }), _jsxs("span", { style: { whiteSpace: "nowrap", marginLeft: "5px" }, children: [valueLength || 0, " / ", charactersLimit] })] }));
        }
        else {
            return helperText;
        }
    }, [helperText, charactersLimit, textLengthForCharacterLimit, numOfMentionsRef.current]);
    const onKeyDown = (event) => {
        var _a;
        if (charactersLimit) {
            const allowedKeys = [
                "Backspace",
                "ArrowUp",
                "ArrowDown",
                "ArrowLeft",
                "ArrowRight",
                "Home",
                "End",
                "PageUp",
                "PageDown",
                "Delete"
            ];
            const textLength = quillRef.current.getEditor().getLength();
            if (textLength - numOfMentionsRef.current - 1 >= charactersLimit && !allowedKeys.includes(event.key)
                && !event.ctrlKey && !event.altKey && !event.metaKey) {
                event.preventDefault();
            }
        }
        (_a = onKeyDownCallbackRef.current) === null || _a === void 0 ? void 0 : _a.call(onKeyDownCallbackRef, event);
    };
    const fullPronunciationConfiguration = ((pronunciationConfiguration === null || pronunciationConfiguration === void 0 ? void 0 : pronunciationConfiguration.enablePronunciation) === true)
        ? Object.assign(Object.assign({}, pronunciationConfiguration), { onEditorBlur: () => {
                quillRef.current.getEditor().blur();
                handleUnFocus();
            } }) : pronunciationConfiguration;
    return (_jsxs(_Fragment, { children: [_jsx(GlobalStyles, { styles: fontFaces }), _jsx(ClickAwayListener, { onClickAway: handleClickAway, mouseEvent: "onMouseDown", children: _jsxs(Box, { sx: { height: "100%" }, children: [_jsxs(FormControl, { "data-testid": dataTestId, "data-tracking-id": dataTrackingId, sx: textFieldSxProps(isTextFieldFocused || isTextFieldExpanded, disabled, error), error: error, onKeyDownCapture: onKeyDown, children: [_jsx(InputLabel, { shrink: true, variant: "outlined", children: isTextFieldExpanded ? "" : label }), _jsxs(Box, { sx: quillWrapperSxProps(joinedFonts, isTextFieldExpanded), children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", flexGrow: 1 }, children: [_jsx(ReactQuill, { id: `rich-text-field-${id}`, ref: quillRef, theme: "default", readOnly: disabled, onChange: changeHandler, onFocus: handleFocus, onBlur: handleBlur, value: value, modules: modules, formats: formats, bounds: `#rich-text-field-wrapper-${id}`, placeholder: placeholder, style: { height: "100%", width: "100%", overflowY: "auto" } }), endAdornment] }), includeToolbar &&
                                            _jsx(RichTextFieldCustomToolbar, { show: isTextFieldFocused || isTextFieldExpanded, id: id, quillRef: quillRef, formats: formats, fonts: fonts, defaultFont: defaultFont, defaultFontSize: defaultFontSize, defaultColor: defaultColor, defaultBold: defaultBold, defaultItalic: defaultItalic, predefinedColors: predefinedColors, enableExpansion: enableExpansion, isTextFieldExpanded: isTextFieldExpanded, handleExpand: handleExpand, pronunciationConfiguration: fullPronunciationConfiguration, speedConfiguration: speedConfiguration, aiTextEditingConfiguration: aiTextEditingConfiguration }), _jsx(NotchedOutline, { className: "rich-text-field", label: isTextFieldExpanded ? "" : label, notched: true })] }), _jsx(FormHelperText, { children: helperTextWithCounterIfNeeded })] }), (mentionConfiguration === null || mentionConfiguration === void 0 ? void 0 : mentionConfiguration.enableMentions) ? _jsx(QuillMentionMenu, { quillRef: quillRef, mentionMenuOpen: mentionMenuOpen, closeMenu: closeMentionMenu, searchTerm: mentionSearchTerm, mentionMenuPositionOffset: mentionMenuPositionOffsetRef.current, mentionOptions: mentionConfiguration.mentionOptions, dataTestId: dataTestId, emptySuggestionElement: mentionConfiguration.emptySuggestionElement, suggestionTrackingId: mentionConfiguration.suggestionTrackingId, onKeyDownCallbackRef: onKeyDownCallbackRef }) : null] }) })] }));
};
//# sourceMappingURL=QuillRichText.js.map